import { Book as BookIcon, Add as PlusIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { Novel } from '@/lib/types';

type NovelCardProps = {
  novel?: Novel;
  onSelect?: (novelId: string) => void;
  isCreateCard?: boolean;
  onCreate?: () => void;
  isExample?: boolean;
  onDismiss?: () => void;
  entityCounts?: {
    characters: number;
    locations: number;
    organizations: number;
    events: number;
  };
};

export function NovelCard({
  novel,
  onSelect,
  isCreateCard = false,
  onCreate,
  isExample = false,
  onDismiss,
  entityCounts,
}: NovelCardProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    if (isCreateCard && onCreate) {
      onCreate();
    } else if (novel && onSelect) {
      onSelect(novel.id);
    }
  };

  if (isCreateCard) {
    return (
      <Card
        sx={{
          height: '100%',
          minHeight: 200,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          borderRadius: 3,
          border: '2px solid',
          borderColor: 'primary.main',
          bgcolor: 'rgba(161, 130, 74, 0.05)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px -10px rgba(161, 130, 74, 0.3)',
            borderColor: 'primary.light',
            bgcolor: 'rgba(161, 130, 74, 0.1)',
          },
        }}
      >
        <CardActionArea
          onClick={handleClick}
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlusIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
              {t('dashboard.novel_card.create_new')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t('dashboard.novel_card.start_building')}
            </Typography>
          </Stack>
        </CardActionArea>
      </Card>
    );
  }

  if (!novel?.id) return null;

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 200,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px -10px rgba(0,0,0,0.12)',
          borderColor: 'primary.light',
        },
      }}
    >
      <CardActionArea onClick={handleClick} sx={{ height: '100%', p: 3 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(161, 130, 74, 0.1)',
                }}
              >
                <BookIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {novel.title}
                  </Typography>
                  {isExample && (
                    <Chip
                      label={t('example_novel.badge')}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(161, 130, 74, 0.15)',
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 20,
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            { //Unreachable code
              entityCounts && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={t('dashboard.novel_card.characters', { count: entityCounts.characters })}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                  <Chip
                    label={t('dashboard.novel_card.locations', { count: entityCounts.locations })}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                  <Chip
                    label={t('dashboard.novel_card.organizations', { count: entityCounts.organizations })}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                  <Chip
                    label={t('dashboard.novel_card.events', { count: entityCounts.events })}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                </Stack>
              )}

            {isExample && onDismiss && (
              <Button
                variant="text"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss();
                }}
                sx={{
                  alignSelf: 'flex-start',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  p: 0,
                  minWidth: 0,
                  '&:hover': { color: 'error.main', bgcolor: 'transparent' },
                }}
              >
                {t('example_novel.hide')}
              </Button>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
